import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CTA } from '@/constants/testIds';

/**
 * Refined "Book a strategy call" + "See how it works" CTA pair.
 * Cleaner, corporate styling: solid button with subtle shadow, no neon glow.
 * Variants:
 *   - tone="dark"  (default) — for use on dark backgrounds (white outline secondary)
 *   - tone="light" — for use on light backgrounds (dark outline secondary)
 *   - size="md" | "sm"
 */
export default function CTAPair({
  align = 'left',
  testIdSuffix = '',
  className = '',
  secondaryTarget,
  tone = 'dark',
  size = 'md',
}) {
  const justify =
    align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';

  const padding = size === 'sm' ? 'px-5 py-3 text-sm' : 'px-6 py-3.5 text-[15px]';

  const primary =
    `inline-flex items-center gap-2 ${padding} rounded-full font-semibold text-white bg-[#FF5081] ` +
    `hover:bg-[#ff3a73] hover:-translate-y-px transition-all duration-200 ` +
    `shadow-[0_8px_24px_-12px_rgba(255,80,129,0.55)]`;

  const secondary =
    tone === 'light'
      ? `inline-flex items-center gap-2 ${padding} rounded-full font-semibold text-[#031D44] border border-[#031D44]/15 hover:border-[#FF5081]/50 hover:text-[#FF5081] transition-all duration-200`
      : `inline-flex items-center gap-2 ${padding} rounded-full font-semibold text-white border border-white/15 hover:border-white/35 hover:bg-white/[0.04] transition-all duration-200`;

  const handleSecondary = (e) => {
    if (!secondaryTarget) return;
    if (secondaryTarget.startsWith('#')) {
      const el = document.getElementById(secondaryTarget.slice(1));
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-3 ${justify} ${className}`}>
      <Link
        to="/contact#contact-form"
        data-testid={`${CTA.primary}${testIdSuffix ? '-' + testIdSuffix : ''}`}
        className={primary}
      >
        Book a strategy call
        <ArrowRight className="w-4 h-4" />
      </Link>
      {secondaryTarget?.startsWith('#') ? (
        <a
          href={secondaryTarget}
          onClick={handleSecondary}
          data-testid={`${CTA.secondary}${testIdSuffix ? '-' + testIdSuffix : ''}`}
          className={secondary}
        >
          See how it works
        </a>
      ) : (
        <Link
          to={secondaryTarget || '/services#how-it-works'}
          data-testid={`${CTA.secondary}${testIdSuffix ? '-' + testIdSuffix : ''}`}
          className={secondary}
        >
          See how it works
        </Link>
      )}
    </div>
  );
}
